import { Routes } from '@angular/router';
import { AppRouting } from '@core/enums';
import { LayoutComponent } from '@core/components/layout/layout.component';
import { authGuard } from '@core/guards/auth.guard';
import { CmsPageUrl } from '@core/models';
import { limitedGuard } from '@core/guards/limited.guard';
import { jottaGuard } from '@core/guards/jotta.guard';
import { dataOnlyGuard } from '@core/guards/data-only.guard';
import { b2bGuard } from '@core/guards/b2b.guard';
import { portingBlockGuard } from '@core/guards/porting-block.guard';
import { internetRestrictionGuard } from '@core/guards/home-internet.guard';

export const routes: Routes = [
  {
    path: AppRouting.Login,
    loadChildren: () => import('../modules/login/login.module').then((m) => m.LoginModule),
  },
  {
    path: AppRouting.SignUp,
    loadChildren: () => import('../modules/signup/signup.module').then((m) => m.SignupModule),
  },
  {
    path: AppRouting.ForgotPassword,
    loadChildren: () =>
      import('../modules/forgot-password/forgot-password.module').then(
        (m) => m.ForgotPasswordModule
      ),
  },
  {
    path: AppRouting.ResetPassword,
    loadChildren: () =>
      import('../modules/reset-password/reset-password.module').then((m) => m.ResetPasswordModule),
  },
  {
    path: AppRouting.SetPassword,
    loadChildren: () =>
      import('../modules/set-password/set-password.module').then((m) => m.SetPasswordModule),
  },
  {
    path: AppRouting.Mobile,
    loadChildren: () => import('../modules/mobile/mobile.module').then((m) => m.MobileModule),
  },
  {
    path: `${AppRouting.Settings}/${AppRouting.SimcardSettings}/activate-esim`, // WEBVIEW ONLY
    loadComponent: () =>
      import('../modules/simcard-settings/components/activate-esim/activate-esim.component').then(
        (m) => m.ActivateEsimComponent
      ),
  },
  {
    path: `${AppRouting.Faq}`,
    loadChildren: () => import('../modules/faq/faq.module').then((m) => m.FaqModule),
    canMatch: [b2bGuard],
  },
  {
    path: '',
    children: [
      {
        path: '',
        redirectTo: AppRouting.Overview,
        pathMatch: 'full',
      },

      {
        path: AppRouting.SelectProduct,
        loadComponent: () =>
          import('../modules/overview/components/product-selector/product-selector.component').then(
            (m) => m.ProductSelectorComponent
          ),
      },
      {
        path: AppRouting.ChangeBankAccountNumber,
        loadComponent: () =>
          import('../modules/payment-settings/components/change-bank-account-number/change-bank-account-number.component').then(
            (m) => m.ChangeBankAccountNumberComponent
          ),
        canActivate: [portingBlockGuard],
      },
      {
        path: AppRouting.ContractTransfer,
        loadComponent: () =>
          import('../modules/overview/components/contract-transfer/contract-transfer.component').then(
            (m) => m.ContractTransferComponent
          ),
        canActivate: [portingBlockGuard],
      },
      {
        path: AppRouting.Overview,
        loadChildren: () =>
          import('../modules/overview/overview.module').then((m) => m.OverviewModule),
        data: {
          cmsPageUrl: CmsPageUrl.Overview,
        },
        canActivate: [portingBlockGuard],
      },
      {
        path: AppRouting.LimitedUser,
        loadChildren: () =>
          import('../modules/limited-user/limited-user.module').then((m) => m.LimitedUserModule),
      },
      {
        path: AppRouting.Products,
        loadChildren: () =>
          import('../modules/subscription/subscription.module').then((m) => m.SubscriptionModule),
        data: {
          cmsPageUrl: CmsPageUrl.Subscription,
        },
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Products}/${AppRouting.Usage}`,
        loadChildren: () => import('../modules/usage/usage.module').then((m) => m.UsageModule),
        data: {
          cmsPageUrl: CmsPageUrl.Usage,
        },
        canMatch: [internetRestrictionGuard],
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Products}/${AppRouting.TopUp}`,
        loadChildren: () => import('../modules/top-up/top-up.module').then((m) => m.TopUpModule),
        data: {
          cmsPageUrl: CmsPageUrl.TopUp,
        },
        canMatch: [b2bGuard, internetRestrictionGuard],
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Products}/${AppRouting.ChangeSubscription}`,
        loadChildren: () =>
          import('../modules/change-subscription/change-subscription.module').then(
            (m) => m.ChangeSubscriptionModule
          ),
        data: {
          cmsPageUrl: CmsPageUrl.ChangeSUbscription,
        },
        canMatch: [b2bGuard],
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Products}/${AppRouting.DataOutsideEu}`,
        loadChildren: () =>
          import('../modules/data-outside-eu/data-outside-eu.module').then(
            (m) => m.DataOutsideEuModule
          ),
        data: {
          cmsPageUrl: CmsPageUrl.DataOutsideEu,
        },
        canMatch: [b2bGuard, internetRestrictionGuard],
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Products}/${AppRouting.PremiumWallet}`,
        loadChildren: () =>
          import('../modules/premium-wallet/premium-wallet.module').then(
            (m) => m.PremiumWalletModule
          ),
        data: {
          cmsPageUrl: CmsPageUrl.PremiumWallet,
        },
        canMatch: [b2bGuard, internetRestrictionGuard],
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Products}/${AppRouting.Prolongation}`,
        loadChildren: () =>
          import('@src/modules/prolongation/prolongation.module').then((m) => m.ProlongationModule),
        data: {
          cmsPageUrl: CmsPageUrl.Prolongation,
        },
        canMatch: [internetRestrictionGuard],
        canActivate: [limitedGuard],
      },
      // {
      //   path: `${AppRouting.Subscription}/${AppRouting.AddSubscription}`,
      //   loadChildren: () =>
      //     import('../modules/add-subscription/add-subscription.module').then(
      //       (m) => m.AddSubscriptionModule
      //     ),
      //   data: {
      //     cmsPageUrl: CmsPageUrl.AddSubscription,
      //   },
      // },
      {
        path: AppRouting.Invoices,
        loadChildren: () =>
          import('../modules/invoices/invoices.module').then((m) => m.InvoicesModule),
        data: {
          cmsPageUrl: CmsPageUrl.Invoices,
        },
        canMatch: [b2bGuard],
        canActivate: [portingBlockGuard],
      },
      {
        path: `${AppRouting.Invoices}/${AppRouting.PaymentSettings}`,
        loadChildren: () =>
          import('../modules/payment-settings/payment-settings.module').then(
            (m) => m.PaymentSettingsModule
          ),
        data: {
          cmsPageUrl: CmsPageUrl.PaymentSettings,
        },
        canActivate: [portingBlockGuard],
      },
      {
        path: AppRouting.CloudStorage,
        loadChildren: () =>
          import('../modules/cloud-storage/cloud-storage.module').then((m) => m.CloudStorageModule),
        data: {
          cmsPageUrl: CmsPageUrl.CloudStorage,
        },
        canActivate: [jottaGuard],
      },
      {
        path: AppRouting.Settings,
        loadChildren: () =>
          import('../modules/settings/settings.module').then((m) => m.SettingsModule),
        data: {
          cmsPageUrl: CmsPageUrl.Settings,
        },
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Settings}/${AppRouting.CommunicationAndPrivacy}`,
        loadChildren: () =>
          import('../modules/communication-and-privacy/communication-and-privacy.module').then(
            (m) => m.CommunicationAndPrivacyModule
          ),
        data: {
          cmsPageUrl: CmsPageUrl.CommunicationAndPrivacy,
        },
        canActivate: [limitedGuard],
      },
      {
        path: `${AppRouting.Settings}/${AppRouting.SimcardSettings}`,
        loadChildren: () =>
          import('../modules/simcard-settings/simcard-settings.module').then(
            (m) => m.SimcardSettingsModule
          ),
        data: {
          cmsPageUrl: CmsPageUrl.SimcardSettigns,
        },
        canMatch: [internetRestrictionGuard],
      },
      {
        path: `${AppRouting.Settings}/${AppRouting.NetworkSettings}`,
        loadChildren: () =>
          import('../modules/network-settings/network-settings.module').then(
            (m) => m.NetworkSettingsModule
          ),
        data: {
          cmsPageUrl: CmsPageUrl.NetworkSettings,
        },
        canMatch: [dataOnlyGuard, internetRestrictionGuard],
        canActivate: [limitedGuard],

      },
    ],
    component: LayoutComponent,
    canActivate: [authGuard],
  },
];
